import {
  type CSSProperties,
  type ReactNode,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useReportsCatalog } from '@/services/reports';
import {
  ChevronDownIcon,
  HistoryClockIcon,
  LogoutIcon,
  SearchBar,
  Typography,
  UserIcon,
} from '@/components/ui';
import type { SearchResult } from '@/services/search';
import { useClickOutside } from '@/hooks';
import { useMsalAuth } from '@/hooks/useMsalAuth';
import { useAuthStore, useClientSearchStore } from '@/stores';
import { WorkspaceTabsBar } from '@/components/layout/WorkspaceTabsBar';

interface NavChild {
  name: string;
  href: string;
}

interface NavigationItem {
  name: string;
  href: string;
  icon: ReactNode;
  /** Highlight on any route under href (sub-routes), not just an exact match. */
  matchPrefix?: boolean;
  /** Fold-out children (e.g. Reports > each report). */
  children?: NavChild[];
}

const navigation: NavigationItem[] = [
  {
    name: 'Clients',
    href: '/clients',
    icon: (
      <svg
        className="w-5 h-5"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
        />
      </svg>
    ),
  },
  {
    name: 'Contract Actions',
    href: '/contract-actions',
    icon: (
      <svg
        className="w-5 h-5"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
        />
      </svg>
    ),
  },
  {
    name: 'Products',
    href: '/products',
    icon: (
      <svg
        className="w-5 h-5"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
        />
      </svg>
    ),
  },
  {
    // Every assessment across all customers (MPN-175). Who may see it is
    // MPN-176/177 (RBAC); until then every signed-in staff member can.
    name: 'Assessments',
    href: '/assessments',
    icon: (
      <svg
        className="w-5 h-5"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
        />
      </svg>
    ),
  },
  {
    name: 'Tickets',
    href: '/tickets',
    icon: (
      <svg
        className="w-5 h-5"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z"
        />
      </svg>
    ),
  },
  {
    name: 'Credits',
    href: '/credits',
    icon: (
      <svg
        className="w-5 h-5"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M3 10h18M5 6h14a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2zm2 10h4"
        />
      </svg>
    ),
  },
  {
    name: 'Alerts',
    href: '/alerts',
    icon: (
      <svg
        className="w-5 h-5"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
        />
      </svg>
    ),
  },
  {
    // The signed-in staff member's own audit trail (SCRUM-1461).
    name: 'History',
    href: '/history',
    icon: <HistoryClockIcon size={20} />,
  },
  {
    // Cross-client bank-statement request feed for fraud checks (SCRUM-1515).
    // RBAC is deferred, so every signed-in staff member can open it.
    name: 'Bank Account Search',
    href: '/bank-account-search',
    icon: (
      <svg
        className="w-5 h-5"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10z"
        />
      </svg>
    ),
  },
  {
    name: 'Payment Imports',
    href: '/payment-imports',
    // Keeps the item highlighted on /payment-imports/configs and /history.
    matchPrefix: true,
    icon: (
      <svg
        className="w-5 h-5"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5"
        />
      </svg>
    ),
  },
  {
    // Children are filled in at render from the Power BI workspace listing
    // (useReportsCatalog). Until it loads — or when the user has no Power BI
    // access — this is a plain link to /reports, which explains the state.
    name: 'Reports',
    href: '/reports',
    matchPrefix: true,
    icon: (
      <svg
        className="w-5 h-5"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
        />
      </svg>
    ),
  },
  {
    // Refer-a-friend program + campaigns (SCRUM-264). "Other Promotions" from
    // the Figma nav has no backend at all, so it is not listed.
    name: 'Promotions',
    href: '/promotions',
    matchPrefix: true,
    icon: (
      <svg
        className="w-5 h-5"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M10.34 15.84c-.688-.06-1.386-.09-2.09-.09H7.5a4.5 4.5 0 1 1 0-9h.75c.704 0 1.402-.03 2.09-.09m0 9.18c.253.962.584 1.892.985 2.783.247.55.06 1.21-.463 1.511l-.657.38c-.551.318-1.26.117-1.527-.461a20.845 20.845 0 0 1-1.44-4.282m3.102.069a18.03 18.03 0 0 1-.59-4.59c0-1.586.205-3.124.59-4.59m0 9.18a23.848 23.848 0 0 1 8.835 2.535M10.34 6.66a23.847 23.847 0 0 0 8.835-2.535m0 0A23.74 23.74 0 0 0 18.795 3m.38 1.125a23.91 23.91 0 0 1 1.014 5.395m-1.014 8.855c-.118.38-.245.754-.38 1.125m.38-1.125a23.91 23.91 0 0 0 1.014-5.395m0-3.46c.495.413.811 1.035.811 1.73 0 .695-.316 1.317-.811 1.73m0-3.46a24.347 24.347 0 0 1 0 3.46"
        />
      </svg>
    ),
    children: [{ name: 'Refer a Friend', href: '/promotions/refer-a-friend' }],
  },
  {
    name: 'Admin',
    href: '/admin',
    icon: (
      <svg
        className="w-5 h-5"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
        />
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
        />
      </svg>
    ),
  },
];

/** A nav item with a fold-out list of children (e.g. Reports > each report). */
function NavSection({
  item,
  currentPath,
  onNavigate,
}: {
  item: NavigationItem;
  currentPath: string;
  onNavigate: () => void;
}) {
  const sectionActive = currentPath.startsWith(item.href);
  const [open, setOpen] = useState(sectionActive);

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen(prev => !prev)}
        aria-expanded={open}
        className={`flex items-center gap-2.5 px-6 py-3 w-full transition-all ${
          sectionActive ? 'bg-primary-1' : 'hover:bg-neutral-50'
        }`}
      >
        <span
          className={`w-5 h-5 flex-shrink-0 ${
            sectionActive ? 'text-primary-6' : 'text-neutral-n500'
          }`}
        >
          {item.icon}
        </span>
        <Typography
          variant="body-medium"
          as="span"
          className={`flex-1 text-left ${
            sectionActive ? 'text-primary-6' : 'text-neutral-n500'
          }`}
        >
          {item.name}
        </Typography>
        <ChevronDownIcon
          size={16}
          className={`flex-shrink-0 transition-transform ${
            open ? '' : '-rotate-90'
          } ${sectionActive ? 'text-primary-6' : 'text-neutral-n500'}`}
        />
      </button>

      {open && (item.children?.length ?? 0) > 0 && (
        <div className="flex flex-col">
          {item.children?.map(child => {
            const childActive = currentPath === child.href;
            return (
              <NavLink
                key={child.href}
                to={child.href}
                onClick={onNavigate}
                className={`flex items-center py-2.5 pl-[52px] pr-6 transition-all ${
                  childActive ? 'bg-primary-1' : 'hover:bg-neutral-50'
                }`}
              >
                <Typography
                  variant="body-medium"
                  as="span"
                  className={
                    childActive ? 'text-primary-6' : 'text-neutral-n500'
                  }
                >
                  {child.name}
                </Typography>
              </NavLink>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function AppShellLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { signOut } = useMsalAuth();
  const user = useAuthStore(state => state.user);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const sidebarRef = useRef<HTMLElement>(null);

  // Derive display values from MSAL account info
  const displayName = user?.name || user?.email || 'User';
  const initials = displayName
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0]?.toUpperCase() ?? '')
    .join('');

  const closeDropdown = useCallback(() => setIsDropdownOpen(false), []);
  useClickOutside(dropdownRef, closeDropdown, isDropdownOpen);

  const closeSidebar = useCallback(() => setIsSidebarOpen(false), []);
  useClickOutside(sidebarRef, closeSidebar, isSidebarOpen);

  // Reports > one entry per report in the Power BI workspace.
  const { data: reports } = useReportsCatalog();
  const navItems = useMemo<NavigationItem[]>(
    () =>
      navigation.map(item =>
        item.href === '/reports'
          ? {
              ...item,
              children: (reports ?? []).map(r => ({
                name: r.name,
                href: `/reports/${r.slug}`,
              })),
            }
          : item
      ),
    [reports]
  );

  const handleLogout = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Logout failed:', error);
      navigate('/login');
    }
  };

  const clientSearchQuery = useClientSearchStore(state => state.query);
  const setClientSearchQuery = useClientSearchStore(state => state.setQuery);

  const handleSearchResultSelect = (result: SearchResult) => {
    switch (result.entityType.toLowerCase()) {
      case 'customer':
      case 'client':
        navigate(`/clients/${result.userId}`);
        break;
      case 'advance':
      case 'product':
        navigate(`/advances/${result.userId}`);
        break;
      case 'task':
        navigate(`/tasks/${result.userId}`);
        break;
      default:
        break;
    }
  };

  const handleNavClick = () => {
    setIsSidebarOpen(false);
  };

  // Routes that render the sticky workspace tab strip. When present, page-level
  // sticky tabs must offset below it (40px strip under the 56px header = 6rem).
  const showWorkspaceTabs =
    location.pathname.startsWith('/clients') ||
    location.pathname.startsWith('/products') ||
    location.pathname.startsWith('/assessments');

  // Search is only offered on the client master page (SCRUM-1109) — it both
  // suggests customers and, on Enter, filters the list below.
  const showSearchBar = location.pathname === '/clients';

  return (
    <div className="min-h-screen bg-lms-background">
      {/* Mobile overlay */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black/30 z-40 lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Fixed full-width Header: logo block over the sidebar column, then search + user */}
      <header className="fixed top-0 left-0 right-0 h-[56px] z-40 flex">
        <div className="hidden lg:flex items-center w-[240px] shrink-0 pl-6 pr-4 bg-white border-b border-r border-neutral-100">
          <span className="font-roboto text-[22px] leading-8 font-bold text-primary-6 whitespace-nowrap">
            LMS Portal
          </span>
        </div>
        <div className="flex flex-1 items-center justify-between bg-white border-b border-neutral-100 px-4 md:px-6">
          {/* Hamburger - mobile only */}
          <button
            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            className="lg:hidden p-2 -ml-2 mr-2 text-neutral-600 hover:text-neutral-900"
            aria-label="Toggle menu"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 6h16M4 12h16M4 18h16"
              />
            </svg>
          </button>

          {/* Search Bar — client master only */}
          {showSearchBar ? (
            <SearchBar
              onResultSelect={handleSearchResultSelect}
              onSubmit={setClientSearchQuery}
              submittedQuery={clientSearchQuery}
              className="flex-1 max-w-[357px]"
            />
          ) : (
            <div className="flex-1" aria-hidden="true" />
          )}

          {/* Right Section */}
          <div className="flex items-center gap-3 md:gap-6 ml-3">
            {/* User Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="flex items-center gap-2 md:gap-3 hover:opacity-80 transition-opacity"
              >
                <div className="w-9 h-9 rounded-full bg-primary-6 flex items-center justify-center">
                  {initials ? (
                    <span className="font-roboto text-white text-sm font-semibold leading-none">
                      {initials}
                    </span>
                  ) : (
                    <UserIcon size={18} />
                  )}
                </div>
                <div className="text-left hidden sm:block">
                  <Typography
                    variant="body-bold"
                    as="p"
                    className="text-neutral-n600"
                  >
                    {displayName}
                  </Typography>
                  {user?.email && user.email !== displayName && (
                    <Typography
                      variant="footnote-description"
                      as="p"
                      className="text-neutral-n200"
                    >
                      {user.email}
                    </Typography>
                  )}
                </div>
                <ChevronDownIcon size={20} className="hidden sm:block" />
              </button>

              {/* Dropdown Menu */}
              {isDropdownOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-neutral-100 py-1 z-50">
                  <button
                    onClick={handleLogout}
                    className="w-full text-left px-4 py-2 text-neutral-700 hover:bg-neutral-50 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <LogoutIcon size={16} />
                      <Typography
                        variant="body-regular"
                        as="span"
                        className="text-neutral-700"
                      >
                        Logout
                      </Typography>
                    </div>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Sidebar - below the header, per Figma. Header is 56px (Figma 72px) —
          compact-density decision 2026-08-26; keep every 56px here, in
          WorkspaceTabsBar and in --content-tabs-top in sync. */}
      <aside
        ref={sidebarRef}
        className={`fixed left-0 top-[56px] w-[240px] h-[calc(100vh-56px)] bg-white border-r border-neutral-100 z-50 transition-transform duration-200 ${
          isSidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } lg:translate-x-0`}
      >
        {/* Navigation */}
        <nav className="py-2.5 flex flex-col gap-1">
          {navItems.map(item => {
            if (item.children && item.children.length > 0) {
              return (
                <NavSection
                  key={item.name}
                  item={item}
                  currentPath={location.pathname}
                  onNavigate={handleNavClick}
                />
              );
            }

            const isActive = item.matchPrefix
              ? location.pathname.startsWith(item.href)
              : location.pathname === item.href;
            return (
              <NavLink
                key={item.name}
                to={item.href}
                onClick={handleNavClick}
                className={`flex items-center gap-2.5 px-6 py-3 w-full transition-all ${
                  isActive ? 'bg-primary-1' : 'hover:bg-neutral-50'
                }`}
              >
                <span
                  className={`w-5 h-5 flex-shrink-0 ${
                    isActive ? 'text-primary-6' : 'text-neutral-n500'
                  }`}
                >
                  {item.icon}
                </span>
                <Typography
                  variant="body-medium"
                  as="span"
                  className={isActive ? 'text-primary-6' : 'text-neutral-n500'}
                >
                  {item.name}
                </Typography>
              </NavLink>
            );
          })}
        </nav>
      </aside>

      {/* Main Content Area */}
      <main
        className="flex min-h-screen flex-col pt-[56px] lg:ml-[240px]"
        style={
          {
            '--content-tabs-top': showWorkspaceTabs ? '6rem' : '3.5rem',
          } as CSSProperties
        }
      >
        {showWorkspaceTabs && <WorkspaceTabsBar />}
        {/* flex-1 (not a fixed min-height) so the workspace tab strip, when present,
            does not push the page past the viewport and force a 48px scroll.
            max-w-[1600px]: page content stops growing on very wide monitors so
            fields keep laptop density; the extra width becomes calm margin
            (user decision 2026-08-31). The shell root paints bg-lms-background,
            so the margin matches the page ground. */}
        <div className="w-full max-w-[1600px] flex-1">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
